import React, { useState } from 'react';
import { X, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const ExcelExportModal = ({ isOpen, onClose, data, columns, filename }) => {
  const [selectedColumns, setSelectedColumns] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  );
  const [selectAll, setSelectAll] = useState(true);

  if (!isOpen) return null;

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const newSelection = columns.reduce((acc, col) => ({ ...acc, [col.key]: checked }), {});
    setSelectedColumns(newSelection);
  };

  const handleColumnToggle = (key) => {
    const newSelection = { ...selectedColumns, [key]: !selectedColumns[key] };
    setSelectedColumns(newSelection);
    setSelectAll(Object.values(newSelection).every(v => v));
  };

  const handleExport = () => {
    // Filter columns based on selection
    const columnsToExport = columns.filter(col => selectedColumns[col.key]);
    
    if (columnsToExport.length === 0) {
      alert('Please select at least one column to export');
      return;
    }

    // Prepare data for export
    const exportData = data.map(row => {
      const exportRow = {};
      columnsToExport.forEach(col => {
        exportRow[col.label] = col.format ? col.format(row[col.key]) : row[col.key] || '';
      });
      return exportRow;
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');

    // Auto-size columns
    const maxWidth = 50;
    const colWidths = columnsToExport.map(col => {
      const header = col.label.length;
      const maxContent = Math.max(
        ...data.map(row => {
          const val = col.format ? col.format(row[col.key]) : row[col.key];
          return String(val || '').length;
        })
      );
      return { wch: Math.min(Math.max(header, maxContent) + 2, maxWidth) };
    });
    ws['!cols'] = colWidths;

    // Generate file
    XLSX.writeFile(wb, `${filename}.xlsx`);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-semibold">Export to Excel</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <p className="text-sm text-gray-600 mb-4">
            Select the columns you want to include in the Excel export:
          </p>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="mr-2 w-4 h-4"
              />
              <span className="font-medium">Select All Columns</span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {columns.map((col) => (
              <label key={col.key} className="flex items-center cursor-pointer p-2 hover:bg-gray-50 rounded">
                <input
                  type="checkbox"
                  checked={selectedColumns[col.key]}
                  onChange={() => handleColumnToggle(col.key)}
                  className="mr-2 w-4 h-4"
                />
                <span>{col.label}</span>
              </label>
            ))}
          </div>

          <div className="mt-4 p-3 bg-gray-50 rounded">
            <p className="text-sm text-gray-600">
              <strong>Total records:</strong> {data.length} | 
              <strong className="ml-2">Selected columns:</strong> {Object.values(selectedColumns).filter(v => v).length}
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download size={18} className="mr-2" />
            Export to Excel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExcelExportModal;
